import { Subject, Subscription, of } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
} from 'rxjs/operators';
import { MainService } from '../../services';
import {
  ANNOUNCEMENT_APP,
  HOLISTA_CONSTANT,
  NETWORK_CODES,
} from '../../constants';
import { LocationUtils, StorageUtility, HolistaUtils } from 'src/app/utils';
import { SearchModalComponent } from 'src/app/modals';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { IBundleCategory, ICategory, IFetchLocationResponse } from 'src/app/interfaces';
import { environment } from 'src/environments/environment';
import { IListingSearchFilter, ILocationList } from './interface';
import { SelectedPlace } from '../location-autocomplete/interface';
const appleQr = '../../../assets/img/jpg/ios-qr.jpeg';
const androidQr = '../../../assets/img/png/android-qr.png';
const googlePlayIcon = '../../../assets/img/png/google-play-badge.png';
const applePlayIcon = '../../../assets/img/png/apple-store-badge.png';
const appPreview = '../../../assets/img/png/app-preview.png';

@Component({
  selector: 'app-main-body',
  templateUrl: './main-body.component.html',
  styleUrls: ['./main-body.component.scss'],
})
export class MainBodyComponent implements OnInit {
  mainSearchForm: FormGroup;
  searchBundleText = new Subject();
  searchProviderText = new Subject();
  searchLocationText = new Subject();
  modalRef: MdbModalRef<SearchModalComponent> | null = null;
  notFound = false;
  mainSearchFormData: IListingSearchFilter;
  networkCode: string;
  options = [
    { value: '1', label: 'Provider' },
    { value: '2', label: 'Facility' },
  ];
  bundleList: any[] = [];
  providerList: any[] = [];
  locationList: ILocationList[] = [];
  showCategoriesAndAppAnnouncement = true;
  isLoading = {
    bundles: false,
    locations: false,
    providers: false,
    categories: false,
  };
  providerSearchParams = {
    latitude: 43.0113,
    longitude: -87.958
  };
  googlePlayLink = ANNOUNCEMENT_APP.GOOGLE_STORE;
  appleStoreLink = ANNOUNCEMENT_APP.APPLE_STORE;
  DEFAULT_RADIUS = HOLISTA_CONSTANT.DEFAULT_RADIUS;
  PAGINATION_QUERY = {
    ...HOLISTA_CONSTANT.defaultPaginationQuery,
  };
  backupBundle: any;
  appleQr = appleQr;
  androidQr = androidQr;
  googlePlayIcon = googlePlayIcon;
  applePlayIcon = applePlayIcon;
  appPreview = appPreview;
  backupProvider: any;
  isSmallScreen: boolean = false;
  isLargeScreen: boolean = false;
  displayClearButton = {
    provider: false,
    service: false,
  };
  isSearchByProvider: boolean = false;
  isSearchClicked: boolean = false;
  radius = HOLISTA_CONSTANT.RADIUS;
  isFormSubmitted: boolean = false;
  HOLISTA_CONSTANT = this._holistaUtility.deepClone(HOLISTA_CONSTANT);
  isLocationInputChanged: boolean = false;
  isHeaderFixed = false; // Controls fixed positioning
  lastScrollY = 0;
  categoryData = [];
  selectedCategory: IBundleCategory;
  selectedSubCategory: string;
  // displayedCategories: ICategory[] = [...this.categoryData];
  // tempDisplayedCategories: ICategory[] = [...this.categoryData];
  // hiddenChips: ICategory[] | string[] = [];
  // displayedSubCategories: string[] = [];
  // tempDisplayedSubCategories: string[] = [];
  // showHiddenChips: boolean = false;
  // chipContainerHeight: any;
  categoriesDropdownList: ICategory[] = [];
  isCategorySliderAtStart: boolean = true;
  isCategorySliderAtEnd: boolean = false;
  locationSubscription: Subscription;
  isOverflowing: boolean = false;
  hasWCDomainPrefix: boolean = false;

  @ViewChild('chipContainer', { static: false }) chipContainer!: ElementRef;

  @Output() isMainPage = new EventEmitter<any>();
  @Output() onViewportHeightChange = new EventEmitter<any>();

  constructor(
    private _formBuilder: FormBuilder,
    private _mainService: MainService,
    private _locationUtils: LocationUtils,
    private _storageUtility: StorageUtility,
    private _holistaUtility: HolistaUtils,
    private modalService: MdbModalService,
    private _cdr: ChangeDetectorRef
  ) {
    this.handleSelectCurrentLocationClick();
    this.setMainSearchForm();
    this.mainSearchFormData = this.mainSearchForm.value;
  }

  ngOnInit(): void {
    this.hasWCDomainPrefix =
      'domainPrefix' in environment && !!environment['domainPrefix'];
    this.isSmallScreen = window.innerWidth <= 676;
    this.isLargeScreen = window.innerWidth > 1280;

    // TODO: If no specific network is required
    // this.networkCode =
    //   this._storageUtility.get('local', 'networkCode') || NETWORK_CODES;

    // this.networkCode = this.mainSearchForm.get('searchByState').value;

    this.locationSubscription = this.mainSearchForm.controls[
      'location'
    ].valueChanges.subscribe((value: ILocationList) => {
      if (typeof value !== 'string') {
        this.onLocationOptionSelected(value);
      }
    });
    this.setDefaultRadius();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.isSmallScreen = window.innerWidth <= 676;
    if (window.innerWidth > 575 && this.modalRef) {
      this.modalRef.close();
    }
  }

  @ViewChild('searchListing') searchListing!: ElementRef;
  searchListingHeight: number = 0;

  ngAfterViewInit() {
    this.calculateHeight();
    this._cdr.detectChanges();
    // window.addEventListener('resize', () => this.adjustDisplayedChips());
  }

  ngOnDestroy() {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  // Check if the chip container content is overflowing horizontally
  checkOverflow() {
    const container = this.chipContainer?.nativeElement;
    if (container) {
      this.isOverflowing = container.scrollWidth > container.clientWidth;
    }
  }

  private calculateHeight() {
    if (this.searchListing?.nativeElement) {
      // Get the height of the element
      this.searchListingHeight = this.searchListing.nativeElement.offsetHeight;
      let extraPixels = 0;

      if (this.isSearchByProvider) {
        extraPixels = this.isSmallScreen ? 320 : 252;
      } else {
        extraPixels = this.isSmallScreen
          ? this.categoryData?.length || this.isLoading.categories
            ? 352
            : 316
          : this.categoryData?.length || this.isLoading.categories
            ? 290
            : 252;
      }

      this.onViewportHeightChange.emit(
        `${+this.searchListingHeight + extraPixels}px`
      );
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const currentScrollY = window.scrollY || document.documentElement.scrollTop;
    this.isHeaderFixed = this.isSmallScreen
      ? currentScrollY > 50
      : currentScrollY > 100;
    this._cdr.detectChanges(); // Force change detection
  }

  // @HostListener('document:keypress', ['$event'])
  // handleKeyboardEvent(event: KeyboardEvent) {
  //   if (event.key === 'Enter' && this.mainSearchForm.valid) {
  //     this.networkCode =
  //       this._storageUtility.get('local', 'networkCode') || NETWORK_CODES;
  //     this.mainSearchForm.controls['searchClicked'].setValue(true);
  //     this.mainSearchForm.controls['networkCode'].setValue(this.networkCode);
  //     this.mainSearchFormData = this.mainSearchForm.value;
  //     this.searchProviders();
  //   }
  // }

  // getChipContainerHeight() {
  //   if (this.chipContainer) {
  //     setTimeout(() => {
  //       this.chipContainerHeight =
  //         this.chipContainer.nativeElement.offsetHeight;
  //     }, 0);
  //   }
  // }

  onChipContainerScroll() {
    const container = this.chipContainer.nativeElement;
    this.isCategorySliderAtStart = container.scrollLeft === 0;
    this.isCategorySliderAtEnd =
      container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
  }

  getCategories() {
    this.isOverflowing = false;
    this.isLoading.categories = true;
    this._mainService
      .getCategories({
        latitude: this.providerSearchParams['latitude'],
        longitude: this.providerSearchParams['longitude'],
        radius: this.mainSearchForm.get('radius').value,
        networks: this.networkCode,
        type: this.isSearchByProvider ? 'PHYSICIAN' : 'FACILITY',
      })
      .pipe(
        finalize(() => {
          this.isLoading.categories = false;
          this.calculateHeight();
        })
      )
      .subscribe({
        next: (response) => {
          const { success, data } = response;
          if (data?.rows?.length > 0) {
            this.categoryData = data.rows.filter(
              (datum) => datum.name && datum.name.trim() !== ''
            );
            this.getCategorizedActiveServices();
            setTimeout(() => {
              this.checkOverflow();
              this.onScrollToElement(
                this.mainSearchForm.get('bundle').value?.toLowerCase()
              );
            }, 0);

            // The timeout is set to allow a brief delay for the view container to initialize
            // setTimeout(() => {
            //   this.adjustDisplayedChips();
            // }, 0);
          }
        },
        error: (error) => {
          console.log('Error getting categories', error);
        },
      });
  }

  clearAutocompleteSearch() {
    if (!this.isSearchByProvider) {
      this.searchBundleText.next('');
      this.mainSearchForm.controls['bundle'].setValue(null);
      this.displayClearButton.service = false;
    } else {
      this.searchProviderText.next('');
      this.mainSearchForm.controls['provider'].setValue(null);
      this.displayClearButton.provider = false;
    }
  }

  onLocationFocusOut() {
    if (this.isLocationInputChanged) {
      this.mainSearchForm.controls['location'].setValue(null);
    }
  }

  onInputChange(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.isLocationInputChanged = true;
  }

  setMainSearchForm() {
    this.mainSearchForm = this._formBuilder.group({
      searchByState: ['HPS'],
      searchBy: ['service'],
      provider: [null],
      bundle: [null, Validators.required],
      location: [null, Validators.required],
      searchClicked: [false],
      optionClicked: [false],
      providers: [null],
      physicians: [null],
      radius: [null],
      physicianId: [null],
      pagination: [null],
      networkCode: [null],
    });
  }

  fetchBundle(keyword: string) {
    this.isLoading.bundles = true;
    this.bundleList.length = 0;
    this.notFound && (this.notFound = false);
    this._mainService
      .getBundles({
        keyword,
        networks: this.networkCode,
        associatedWithProviderOnly: true,
      })
      .pipe(
        finalize(() => {
          this.isLoading.bundles = false;
        })
      )
      .subscribe({
        next: (response) => {
          const { data, success } = response;
          if (success) {
            if (data?.rows?.length) {
              const allBundleDisplayNames = data.rows.map(
                (bundle) => bundle.displayName
              );
              const uniqueBundlesByDisplayName = [
                ...new Set(allBundleDisplayNames),
              ];
              this.bundleList = [];
              uniqueBundlesByDisplayName.forEach((uniqueBundle) => {
                const bundles = data.rows.filter(
                  (data) => data.displayName === uniqueBundle
                );
                this.bundleList.push({
                  displayName: bundles[0]?.displayName,
                  bundleName: bundles[0]?.bundleName,
                  // serviceBundleId: bundles[0]?.serviceBundleId,
                  serviceBundleId: bundles
                    .map((bundle) => bundle.serviceBundleId)
                    .join(','),
                  label: bundles[0]?.displayName || bundles[0]?.bundleName,
                  value: bundles[0]?.bundleCode,
                });
              });
              // this.bundleList = data.rows.map((bundle: any) => ({
              //   // ...bundle,
              //   displayName: bundle?.displayName,
              //   bundleName: bundle?.bundleName,
              //   serviceBundleId: bundle?.serviceBundleId,
              //   label: `${bundle?.displayName || bundle?.bundleName} ${BUNDLE_NAME_CONCAT_STRING[bundle?.network?.code]}`,
              //   value: bundle?.bundleCode,
              // }));
            } else this.notFound = true;
          }
        },
        error: (error) => {
          console.log('Error getting bundles', error);
        },
      });
  }

  fetchProvider(keyword: string) {
    // this.isLoading.providers = true;
    this.providerList.length = 0;
    this.notFound && (this.notFound = false);
    this._mainService
      .getProviders({
        keyword,
        networks: this.networkCode,
      })
      .pipe(
        finalize(() => {
          // this.isLoading.bundles = false;
        })
      )
      .subscribe({
        next: (response) => {
          const { data, success } = response;
          if (success) {
            let secondEntityTypeData: any = [];
            if (data?.length) {
              secondEntityTypeData = data.filter(
                (eachData) =>
                  eachData?.provider &&
                  (eachData.provider.displayName ||
                    eachData.provider.businessName ||
                    eachData.provider.firstName)
              );
            }

            if (secondEntityTypeData?.length) {
              this.providerList = secondEntityTypeData.map((data: any) => ({
                displayName: data?.provider.displayName,
                businessName: data?.provider.businessName,
                fullName: this._holistaUtility.getFullName(
                  data?.provider.firstName,
                  data?.provider.middleName,
                  data?.provider.lastName
                ),
                id: data?.provider.id,
                label:
                  data?.provider.displayName ||
                  data?.provider.businessName ||
                  this._holistaUtility.getFullName(
                    data?.provider.firstName,
                    data?.provider.middleName,
                    data?.provider.lastName
                  ),
                value: data?.provider.id,
                entityType: data?.provider.entityType,
              }));
            } else this.notFound = true;
          }
        },
        error: (error) => {
          console.log('Error getting bundles', error);
        },
      });
  }


  onBundleDisplay = (bundle?: any): string | undefined => {
    if (typeof bundle === 'string') {
      return bundle.length > 45 ? bundle.slice(0, 45) + '...' : bundle;
    }

    if (!bundle) return undefined;
    const value = bundle.label || bundle.displayName || bundle.bundleName;
    return value && value.length > 45 ? value.slice(0, 45) + '...' : value;
  };

  onProviderDisplay = (provider?): string | any => {
    if (typeof provider === 'string') {
      return provider;
    }
    return provider
      ? provider.label ||
      provider.displayName ||
      provider.businessName ||
      undefined
      : undefined;
  };

  onLocationDisplay = (location?): string | undefined => {
    if (typeof location === 'string') {
      return location;
    }
    return location ? location.label : undefined;
  };

  updateMainSearchFilter(event) {
    const {
      state,
      searchBy,
      searchClicked,
      location,
      bundle,
      radius,
      physicianId,
      pagination,
    } = event.searchForm;
    this.mainSearchForm.controls['searchBy'].setValue(searchBy);
    this.mainSearchForm.controls['searchByState'].setValue(state);
    this.mainSearchForm.controls['searchClicked'].setValue(searchClicked);
    this.mainSearchForm.controls['optionClicked'].setValue(
      typeof bundle === 'string' ? false : true
    );
    this.mainSearchForm.controls['bundle'].setValue(bundle);
    bundle && this.onBundleDisplay(bundle);
    this.mainSearchForm.controls['radius'].setValue(radius);
    this.mainSearchForm.controls['physicianId'].setValue(physicianId);
    this.mainSearchForm.controls['location'].setValue(location);
    this.mainSearchForm.controls['pagination'].setValue(pagination);
    if (radius) {
      this.providerSearchParams['radius'] = radius;
    } else {
      delete this.providerSearchParams['radius'];
    }
    if (physicianId) {
      this.providerSearchParams['physicianId'] = physicianId;
    } else {
      delete this.providerSearchParams['physicianId'];
    }
    this.searchProviders();
  }

  onScrollToElement(category: string) {
    setTimeout(() => {
      const element = document.querySelector(`[data-category="${category}"]`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          // inline: 'center',
        });
      }
    }, 0);
  }

  onServiceCategorySelect({
    data,
    isDropdownSelection = false,
  }: {
    data: IBundleCategory;
    isDropdownSelection?: boolean;
  }) {
    this.selectedCategory = data;
    this.mainSearchForm.controls['bundle'].setValue(data.name);
    this.submitMainFilter();
    // A timeout is applied to ensure the width is calculated before the view is initialized

    // TODO: uncomment when show sub categories
    // setTimeout(() => {
    //   this.onToggleExpandHiddenChips({ isCategorySelected: true });
    // }, 0);
    // TODO: uncomment when show sub categories

    if (isDropdownSelection) {
      this.categoriesDropdownList.length = 0;
    }
  }

  onServiceSubCategorySelect({ value }: { value: string }) {
    this.mainSearchForm.controls['bundle'].setValue(
      value.toLowerCase() === 'all' ? this.selectedCategory.name : value
    );
    const bundleValue = this.mainSearchForm.get('bundle').value.toLowerCase();
    // this.selectedSubCategory =
    //   this.selectedCategory.subCategories.find(
    //     (value) => value.toLowerCase() === bundleValue.toLowerCase()
    //   ) || '';
    this.submitMainFilter({ isSubCategorySelected: true });
  }

  getCategorizedActiveServices() {
    const bundleValue = this.mainSearchForm.get('bundle').value?.toLowerCase();

    if (!bundleValue) {
      this.selectedCategory = null;
      return;
    }

    if (this.categoryData?.length) {
      this.selectedCategory =
        this.categoryData.find(
          (data) => data.name.toLowerCase() === bundleValue?.toLowerCase()
        ) || null;
    }

    // TODO: uncomment when show sub categories
    // if (!this.selectedCategory) {
    //   this.selectedCategory = this.categoryData.find(
    //     (item) =>
    //       item.subCategories &&
    //       item.subCategories.some((sub) => sub?.toLowerCase() === bundleValue)
    //   );
    // }
    // this.selectedSubCategory =
    //   this.selectedCategory?.subCategories.find(
    //     (value) => value.toLowerCase() === bundleValue
    //   ) || '';
    // TODO: uncomment when show sub categories
  }

  submitMainFilter({
    isSubCategorySelected,
    isModalSubmit,
  }: {
    isSubCategorySelected?: boolean;
    isModalSubmit?: boolean;
  } = {}) {
    this.isSearchClicked = true;
    if (this.isLocationInputChanged) {
      this.mainSearchForm.controls['location'].setValue(null);
    }

    if (this.mainSearchForm.valid) {
      this.searchListingHeight = 0;
      this.onViewportHeightChange.emit('auto');
      this.isFormSubmitted = true;
      this.showCategoriesAndAppAnnouncement = false;

      if (this.mainSearchForm.value?.pagination) {
        this.mainSearchForm.controls['pagination'].setValue(null);
        this.PAGINATION_QUERY = {
          ...HOLISTA_CONSTANT.defaultPaginationQuery,
        };
      }

      if (this.mainSearchForm.value.location) {
        const { latitude, longitude } = this.mainSearchForm.value.location;

        if (latitude && longitude) {
          this.providerSearchParams['radius'] =
            this.mainSearchForm.get('radius').value;
        }
        this.providerSearchParams['radius'] =
          this.mainSearchForm.get('radius').value;
      } else {
        this.mainSearchForm.controls['radius'].setValue(null);
      }

      if (!this.isSearchByProvider) {
        if (isModalSubmit) {
          this.getCategories();
        }
        this.getCategorizedActiveServices();
      }

      this.mainSearchForm.controls['searchClicked'].setValue(true);
      this.mainSearchForm.controls['networkCode'].setValue(this.networkCode);
      this.mainSearchFormData = this.mainSearchForm.value;
      this.isMainPage.emit(
        !this.mainSearchFormData.searchClicked &&
        !this.mainSearchFormData.optionClicked
      );

      // TODO: uncomment when show sub categories
      // if (!isSubCategorySelected) {
      //   this.adjustDisplayedChips();
      // }
      // TODO: uncomment when show sub categories
      if (!this.isSearchByProvider) {
        this.onScrollToElement(
          this.mainSearchForm.get('bundle').value.toLowerCase()
        );
      }
      this.searchProviders();
    }
  }

  onViewChange() {
    setTimeout(() => {
      this.calculateHeight();
    }, 100);
  }

  searchProviders() {
    this.isLoading.providers = true;
    this.mainSearchForm.controls['providers'].setValue([]);
    this.mainSearchFormData = this.mainSearchForm.value;
    if (this.mainSearchForm.value?.pagination) {
      this.PAGINATION_QUERY = this.mainSearchForm.value.pagination;
    }
    const { totalCount, page, ...rest } =
      this.mainSearchForm.value.pagination || this.PAGINATION_QUERY;
    if (this.isSearchByProvider) {
      // delete this.providerSearchParams['serviceId'];
      if (
        this.providerSearchParams['entityType'] ==
        HOLISTA_CONSTANT.ENTITY_TYPE.PHYSICIAN
      ) {
        delete this.providerSearchParams['facilityId'];
      } else {
        delete this.providerSearchParams['physicianId'];
      }
    } else {
      delete this.providerSearchParams['facilityId'];
      delete this.providerSearchParams['physicianId'];
    }
    const searchQuery = {
      ...this.providerSearchParams,
      ...rest,
      networks: this.networkCode,
      associatedWithProviderOnly: true,
      ...(
        !this.isSearchByProvider && {
          service: this.mainSearchForm.get('bundle').value,
        }
      ),
      ...(this.isSearchByProvider && {
        keyword: this.mainSearchForm.get('provider').value,
      }),
      type: this.isSearchByProvider ? 'PHYSICIAN' : 'FACILITY',
      excludeTerminatedProviders: true,
      sortBy: 'distance',
      sortOrder: 'asc',
    };
    this._mainService.providerExportQuery = { ...searchQuery };
    this._mainService
      .searchProviders(searchQuery)
      .pipe(
        finalize(() => {
          this.isLoading.providers = false;
          this.isSearchClicked = false;
          setTimeout(() => {
            this.calculateHeight();
          }, 100);
        })
      )
      .subscribe({
        next: (response) => {
          if (response && response?.data?.rows?.length > 0) {
            if (this.isSearchByProvider) {
              this.mainSearchForm.controls['providers'].setValue(
                response?.data?.rows || []
              );
            } else {
              this.mainSearchForm.controls['providers'].setValue(
                response?.data?.rows.filter((eachData) => eachData) ||
                []
              );
            }
            this.PAGINATION_QUERY = {
              ...this.PAGINATION_QUERY,
              totalCount: response.data.count,
            };
            this.mainSearchForm.controls['pagination'].setValue(
              this.PAGINATION_QUERY
            );
            this.mainSearchFormData = this.mainSearchForm.value;
            this.isMainPage.emit(
              !this.mainSearchFormData.searchClicked &&
              !this.mainSearchFormData.optionClicked
            );
          }
        },
        error: (error) => {
          console.log('Error getting searched providers.', error);
        },
      });
  }

  onBundleOptionSelected(option: any) {
    if (this.isSearchByProvider) {
      this.displayClearButton.provider = true;
      const { id, entityType, isChipSelected } = option;
      this.providerSearchParams['entityType'] = entityType;
      this.providerSearchParams[
        entityType === HOLISTA_CONSTANT.ENTITY_TYPE.PHYSICIAN
          ? 'physicianId'
          : 'facilityId'
      ] = id;
      this.mainSearchForm.controls['optionClicked'].setValue(true);
      this.mainSearchForm.controls['pagination'].setValue(
        HOLISTA_CONSTANT.defaultPaginationQuery
      );
      if (isChipSelected) {
        this.mainSearchForm.controls['provider'].setValue(option);
        this.mainSearchForm.controls['optionClicked'].setValue(true);
        this.onProviderDisplay(option);
        this.submitMainFilter();
      }
    } else {
      this.displayClearButton.service = true;
      const { serviceBundleId, isChipSelected } = option;
      // this.providerSearchParams['serviceId'] = serviceBundleId;
      this.mainSearchForm.controls['optionClicked'].setValue(true);
      this.mainSearchForm.controls['pagination'].setValue(
        HOLISTA_CONSTANT.defaultPaginationQuery
      );
      if (isChipSelected) {
        this.mainSearchForm.controls['bundle'].setValue(option);
        this.mainSearchForm.controls['optionClicked'].setValue(true);
        this.onBundleDisplay(option);
        this.submitMainFilter();
      }
    }
  }

  setNetworkCode(state: string) {
    switch (true) {
      case this.hasWCDomainPrefix:
        this.networkCode = 'WC';
        break;

      case state === 'WI':
        this.networkCode = 'HPS';
        break;

      case state === 'NC':
        this.networkCode = 'NC';
        break;

      default:
        this.networkCode = 'HPS,NC';
        break;
    }
  }

  setDefaultRadius() {
    this.mainSearchForm.controls['radius'].setValue(
      HOLISTA_CONSTANT.DEFAULT_RADIUS
    );
    this.providerSearchParams['radius'] = HOLISTA_CONSTANT.DEFAULT_RADIUS;
  }

  onLocationOptionSelected(option?: ILocationList) {
    this.isLocationInputChanged = false;
    if (option) {
      this.setNetworkCode(option?.state);
      const { longitude, latitude } = option;
      this.providerSearchParams['latitude'] = latitude;
      this.providerSearchParams['longitude'] = longitude;
      this.mainSearchForm.controls['location'].setValue(option, {
        emitEvent: false,
      });
      if (!this.isSearchByProvider) {
        this.getCategories();
      }
    }
  }

  onRadiusSelected(data) {
    if (!this.isSearchByProvider) {
      this.getCategories();
    }
  }

  handleSelectCurrentLocationClick() {
    this.isLocationInputChanged = false;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const loc: any = await this._locationUtils.getLocationDetails(
            position
          );
          if (loc) {
            this.providerSearchParams['latitude'] = loc.lat;
            this.providerSearchParams['longitude'] = loc.lng;
            this.mainSearchForm.controls['location'].setValue({
              city: loc.city,
              label: loc.displayName,
              latitude: loc.lat,
              longitude: loc.lng,
              state: loc.state,
              value: loc.city,
              zip: loc.zip,
            });
            this.setNetworkCode(loc.state);
          } else {
            this.setNetworkCode(HOLISTA_CONSTANT.DEFAULT_LOCATION.st);
          }
          if (!this.isSearchByProvider) {
            this.getCategories();
          }
          this._cdr.detectChanges();
        },
        (error) => {
          console.log(error.message);
          this.setDefaultLocation();
          this._cdr.detectChanges();
        }
      );
    } else {
      this.setDefaultLocation();
    }
  }

  private setDefaultLocation() {
    const defaultLoc = HOLISTA_CONSTANT.DEFAULT_LOCATION;
    this.providerSearchParams['latitude'] = defaultLoc.latitude;
    this.providerSearchParams['longitude'] = defaultLoc.longitude;
    this.mainSearchForm.controls['location'].setValue({
      city: defaultLoc.city,
      label: defaultLoc.displayName,
      latitude: defaultLoc.latitude,
      longitude: defaultLoc.longitude,
      state: defaultLoc.state,
      value: defaultLoc.city,
      zip: defaultLoc.zip,
    });
    this.setNetworkCode(defaultLoc.st);
    if (!this.isSearchByProvider) {
      this.getCategories();
    }
  }

  openSearchModal() {
    this.modalRef = this.modalService.open(SearchModalComponent, {
      data: {
        mainSearchFormData: this.mainSearchFormData,
        providerSearchParams: this.providerSearchParams,
      },
    });
    this.modalRef.onClose.subscribe((data: any) => {
      if (data) {
        const { mainSearchFormData, providerSearchParams } = data;
        this.isSearchByProvider = mainSearchFormData.searchBy === 'provider';

        if (this.isSearchByProvider) {
          this.alterFormValidationOnProviderSelect();
        } else {
          this.alterFormValidationOnServiceSelect();
        }

        this.providerSearchParams = {
          ...this.providerSearchParams,
          ...providerSearchParams,
        };
        this.mainSearchForm.patchValue(mainSearchFormData);
        this.mainSearchFormData = mainSearchFormData;
        this.submitMainFilter({ isModalSubmit: true });
      }
    });
  }

  alterFormValidationOnProviderSelect() {
    this.mainSearchForm.controls['bundle'].setValue(null);
    this.mainSearchForm.get('provider').setValidators([Validators.required]);
    this.mainSearchForm.get('bundle').clearValidators();
  }

  alterFormValidationOnServiceSelect() {
    this.mainSearchForm.controls['provider'].setValue(null);
    this.mainSearchForm.get('bundle').setValidators([Validators.required]);
    this.mainSearchForm.get('provider').clearValidators();
  }

  changeSearchBy(event) {
    this.mainSearchForm.controls['searchBy'].setValue(event.target.value);
    this.selectedCategory = {} as IBundleCategory;
    this.onViewportHeightChange.emit('auto');
    this.isFormSubmitted = false;
    this.isSearchClicked = false;
    if (event.target.value === 'provider') {
      this.isSearchByProvider = true;
      this.alterFormValidationOnProviderSelect();
      this._mainService.providerExportQuery = {
        type: 'PHYSICIAN',
        excludeTerminatedProviders: true,
      }
    } else {
      this.isSearchByProvider = false;
      this.alterFormValidationOnServiceSelect();
      this._mainService.providerExportQuery = {
        type: 'FACILITY',
        excludeTerminatedProviders: true,
      }
    }
    this.mainSearchForm.get('provider').updateValueAndValidity();
    this.mainSearchForm.get('bundle').updateValueAndValidity();
    this.clearAutocompleteSearch();
  }

  onShowCategoriesDropdown({ data }: { data: ICategory }) {
    if (this.categoriesDropdownList.length) {
      this.categoriesDropdownList.length = 0;
    } else {
      this.categoriesDropdownList = this.categoryData.filter(
        (cat) => cat.category.toLowerCase() !== data.category.toLowerCase()
      );
    }
  }

  onSlideCategories({ direction }: { direction: string }) {
    if (this.chipContainer) {
      const container = this.chipContainer.nativeElement;
      const scrollAmount =
        direction === 'right' ? container.clientWidth : -container.clientWidth;

      container.scrollTo({
        left: container.scrollLeft + scrollAmount,
        behavior: 'smooth',
      });

      setTimeout(() => {
        this.isCategorySliderAtStart = container.scrollLeft === 0;
        this.isCategorySliderAtEnd =
          Math.abs(
            container.scrollLeft + container.clientWidth - container.scrollWidth
          ) <= 1;
      }, 300);
    }
  }

  onKeyDownEnter() { }

  onPlaceSelected(place: SelectedPlace) {
    const getComp = (type: string, short = false) =>
      place.addressComponents?.find((c: any) => c.types.includes(type))?.[short ? 'shortText' : 'longText'];

    const city = getComp('locality') || getComp('postal_town') || getComp('sublocality');
    const state = getComp('administrative_area_level_1', true);
    const zip = getComp('postal_code');

    const location: ILocationList = {
      city,
      latitude: place.location?.lat,
      longitude: place.location?.lng,
      state,
      zip,
      label: place.displayName,
    } as ILocationList;

    this.resetOnLocationChange();
    this.onLocationOptionSelected(location);
  }


  onCurrentLocationSelected(
    place: SelectedPlace
  ): void {

    const loc = place.raw;
    this.isLocationInputChanged = false;
    this.providerSearchParams['latitude'] =
      loc.lat;
    this.providerSearchParams['longitude'] =
      loc.lng;
    this.mainSearchForm.controls['location']
      .setValue({
        city: loc.city,
        label: loc.displayName,
        latitude: loc.lat,
        longitude: loc.lng,
        state: loc.state,
        value: loc.city,
        zip: loc.zip,
      });

    this.setNetworkCode(loc.state);
    if (!this.isSearchByProvider) {
      this.getCategories();
    }

    this._cdr.detectChanges();
    this.resetOnLocationChange();
  }

  private resetOnLocationChange() {
  // clear category selection
  this.selectedCategory = null;
  this.selectedSubCategory = '';
  this.categoryData = [];
  this.categoriesDropdownList.length = 0;
  this.isCategorySliderAtStart = true;
  this.isCategorySliderAtEnd = false;
  if (this.chipContainer?.nativeElement) {
    this.chipContainer.nativeElement.scrollLeft = 0;
  }

  // clear the search term so getCategorizedActiveServices() can't re-select
  this.mainSearchForm.controls['bundle'].setValue(null);
  this.mainSearchForm.controls['provider'].setValue(null);
  this.displayClearButton = { provider: false, service: false };

  // clear the listed data
  this.mainSearchForm.controls['providers'].setValue([]);
  this.mainSearchForm.controls['physicians'].setValue(null);
  this.mainSearchForm.controls['searchClicked'].setValue(false);
  this.mainSearchForm.controls['optionClicked'].setValue(false);
  this.mainSearchForm.controls['pagination'].setValue(null);
  this.PAGINATION_QUERY = { ...HOLISTA_CONSTANT.defaultPaginationQuery };
  delete this.providerSearchParams['physicianId'];
  delete this.providerSearchParams['facilityId'];

  this.isFormSubmitted = false;
  this.isSearchClicked = false;
  this.searchListingHeight = 0;

  this.mainSearchFormData = this.mainSearchForm.value;
  this.isMainPage.emit(true);
  this.onViewportHeightChange.emit('auto');
  this._cdr.detectChanges();
  this.showCategoriesAndAppAnnouncement = true
}
}
